import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { Team } from 'Src/models/team';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import {
  getAllSchoolTeams,
  rolloverSchoolTeams,
  TeamRolloverPreviewRow,
  TeamRolloverRequestRow
} from 'Src/helpers/service/admin/schoolTeams';
import './TeamSeasonRolloverModal.css';

interface Props {
  user: AppUser;
  isOpen: boolean;
  currentSeason: string;
  initialTeam?: Team;
  onClose: () => void;
  onComplete: () => void;
}

interface Draft extends TeamRolloverRequestRow {
  selected: boolean;
  sourceName: string;
}

const previousSeason = (season: string) => {
  const start = Number(season.slice(0, 4)) - 1;
  return `${start}/${String(start + 1).slice(-2)}`;
};

const suggestName = (name: string, sourceSeason: string, targetSeason: string) => {
  const delta = Number(targetSeason.slice(0, 4)) - Number(sourceSeason.slice(0, 4));
  return name.replace(/\bU(\d{1,2})(?=[A-Z]?\b)/gi, (_, age) => `U${Number(age) + delta}`);
};

const makeDraft = (team: Team, sourceSeason: string, targetSeason: string): Draft => {
  const delta = Number(targetSeason.slice(0, 4)) - Number(sourceSeason.slice(0, 4));
  return {
    selected: true,
    teamId: team.id,
    sourceName: team.name,
    name: suggestName(team.name, sourceSeason, targetSeason),
    ages: (team.ages || []).map(age => Number(age) + delta),
    copyPlayers: true,
    copyLeadership: false,
    copyKit: false
  };
};

export const TeamSeasonRolloverModal = ({ user, isOpen, currentSeason, initialTeam, onClose, onComplete }: Props) => {
  const initialSourceSeason = (initialTeam && initialTeam.season) || previousSeason(currentSeason);
  const [sourceSeason, setSourceSeason] = React.useState(initialSourceSeason);
  const [drafts, setDrafts] = React.useState<Draft[]>([]);
  const [preview, setPreview] = React.useState<TeamRolloverPreviewRow[] | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [isDirty, setIsDirty] = React.useState(false);
  const operationId = React.useRef('');

  const sourceYear = Number(currentSeason.slice(0, 4));
  const sourceSeasonOptions = Array.from({ length: 12 }, (_, index) => {
    const start = sourceYear - index - 1;
    return `${start}/${String(start + 1).slice(-2)}`;
  });
  if (initialSourceSeason && !sourceSeasonOptions.includes(initialSourceSeason)) {
    sourceSeasonOptions.unshift(initialSourceSeason);
  }

  React.useEffect(() => {
    if (!isOpen) return;
    setPreview(null);
    setError('');
    setIsDirty(false);
    operationId.current = `team-rollover-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;
    if (initialTeam) {
      setSourceSeason(initialSourceSeason);
      setDrafts([makeDraft(initialTeam, initialSourceSeason, currentSeason)]);
      return;
    }
    setIsLoading(true);
    getAllSchoolTeams(user, { season: sourceSeason })
      .then(teams => setDrafts(teams.map(team => makeDraft(team, sourceSeason, currentSeason))))
      .catch(() => setError('Could not load teams for this season.'))
      .then(() => setIsLoading(false));
  }, [isOpen, initialTeam, sourceSeason, currentSeason]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!preview && drafts.length === 0) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [isOpen, drafts, preview]);

  if (!isOpen) return null;

  const selected = drafts.filter(item => item.selected);
  const requestRows = (): TeamRolloverRequestRow[] =>
    selected.map(({ selected: ignored, sourceName, ...item }) => item);
  const updateDraft = (teamId: string, update: Partial<Draft>) => {
    setPreview(null);
    setIsDirty(true);
    setDrafts(items => items.map(item => (item.teamId === teamId ? { ...item, ...update } : item)));
  };

  const runPreview = () => {
    setIsLoading(true);
    setError('');
    rolloverSchoolTeams(user, {
      mode: 'PREVIEW',
      sourceSeason,
      targetSeason: currentSeason,
      teams: requestRows()
    })
      .then(result => setPreview(result.teams as TeamRolloverPreviewRow[]))
      .catch(err =>
        setError(
          (err && err.response && err.response.data && err.response.data.details && err.response.data.details.text) ||
            'Could not preview team setup.'
        )
      )
      .then(() => setIsLoading(false));
  };

  const execute = () => {
    setIsLoading(true);
    setError('');
    rolloverSchoolTeams(user, {
      mode: 'EXECUTE',
      operationId: operationId.current,
      sourceSeason,
      targetSeason: currentSeason,
      teams: requestRows()
    })
      .then(() => onComplete())
      .catch(err =>
        setError(
          (err && err.response && err.response.data && err.response.data.details && err.response.data.details.text) ||
            'Could not create teams.'
        )
      )
      .then(() => setIsLoading(false));
  };

  const hasConflicts = Boolean(preview && preview.some(item => item.conflict));
  const requestClose = () => {
    if (!isDirty || window.confirm('You have unsaved changes. Discard them?')) onClose();
  };

  return (
    <SimpleModal
      isOpen={isOpen}
      title={initialTeam ? 'Copy team' : `Set up teams for ${currentSeason}`}
      showFooter={false}
      onCloseClick={requestClose}
      customClass="teamRolloverModal"
      isBodyBold={false}
    >
      <form className="teamRollover" onSubmit={event => event.preventDefault()}>
        {!initialTeam && (
          <label className="teamRollover__season">
            Copy teams from
            <select value={sourceSeason} onChange={event => setSourceSeason(event.target.value)}>
              {sourceSeasonOptions.map(season => (
                <option key={season}>{season}</option>
              ))}
            </select>
          </label>
        )}
        <p>
          New teams will be created for <strong>{currentSeason}</strong>. Suggested U-age names and age groups can be
          edited before continuing.
        </p>
        {!initialTeam && drafts.length > 0 && (
          <div className="teamRollover__selectionActions">
            <button
              type="button"
              onClick={() => {
                setPreview(null);
                setIsDirty(true);
                setDrafts(items => items.map(item => ({ ...item, selected: true })));
              }}
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => {
                setPreview(null);
                setIsDirty(true);
                setDrafts(items => items.map(item => ({ ...item, selected: false })));
              }}
            >
              Clear all
            </button>
            <span>
              {selected.length} of {drafts.length} selected
            </span>
          </div>
        )}
        {error && <div className="teamRollover__error">{error}</div>}
        {isLoading ? (
          <div className="teamRollover__loading">Working…</div>
        ) : (
          <div className="teamRollover__tableWrap">
            <table className="teamRollover__table">
              <thead>
                <tr>
                  <th>Copy</th>
                  <th>Previous team</th>
                  <th>New name</th>
                  <th>Age values</th>
                  <th>Players</th>
                  <th>Captain/sub</th>
                  <th>Kit</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map(item => {
                  const result = preview && preview.find(row => row.teamId === item.teamId);
                  return (
                    <tr key={item.teamId} className={result && result.conflict ? 'has-error' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={item.selected}
                          onChange={event => updateDraft(item.teamId, { selected: event.target.checked })}
                        />
                      </td>
                      <td>{item.sourceName}</td>
                      <td>
                        <input
                          value={item.name}
                          disabled={!item.selected}
                          onChange={event => updateDraft(item.teamId, { name: event.target.value })}
                        />
                        {result && result.conflict && <small>Name already exists for this sport and season.</small>}
                      </td>
                      <td>
                        <input
                          value={item.ages.join(', ')}
                          disabled={!item.selected}
                          onChange={event =>
                            updateDraft(item.teamId, {
                              ages: event.target.value
                                .split(',')
                                .map(value => Number(value.trim()))
                                .filter(Number.isFinite)
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={item.copyPlayers}
                          disabled={!item.selected}
                          onChange={event => updateDraft(item.teamId, { copyPlayers: event.target.checked })}
                        />
                        {result && (
                          <small
                            title={result.skippedPlayers
                              .map(player => `${player.firstName || ''} ${player.lastName || ''}`.trim())
                              .filter(Boolean)
                              .join(', ')}
                          >
                            {result.playersToCopy} copied
                            {result.skippedPlayers.length ? `, ${result.skippedPlayers.length} skipped` : ''}
                          </small>
                        )}
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={item.copyLeadership}
                          disabled={!item.selected || !item.copyPlayers}
                          onChange={event => updateDraft(item.teamId, { copyLeadership: event.target.checked })}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={item.copyKit}
                          disabled={!item.selected}
                          onChange={event => updateDraft(item.teamId, { copyKit: event.target.checked })}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && drafts.length === 0 && <p>No teams were found in {sourceSeason}.</p>}
        <div className="teamRollover__actions">
          <button type="button" onClick={requestClose}>
            Cancel
          </button>
          {!preview ? (
            <button
              type="button"
              className="is-primary"
              disabled={selected.length === 0 || selected.some(item => !item.name.trim())}
              onClick={runPreview}
            >
              Review changes
            </button>
          ) : (
            <button type="button" className="is-primary" disabled={hasConflicts || isLoading} onClick={execute}>
              Create {selected.length} team{selected.length === 1 ? '' : 's'}
            </button>
          )}
        </div>
      </form>
    </SimpleModal>
  );
};
